'use client';
import React from 'react';
import { Menu, Sun, Moon, Bell, Search } from 'lucide-react';

interface TopbarProps {
  onToggleSidebar: () => void;
  onToggleMobileSidebar: () => void;
  darkMode: boolean;
  onToggleDark: () => void;
  activeRole: 'executive' | 'sales' | 'operations';
  onRoleChange: (role: 'executive' | 'sales' | 'operations') => void;
}

const roles: { id: 'executive' | 'sales' | 'operations'; label: string }[] = [
  { id: 'executive', label: 'Executive' },
  { id: 'sales', label: 'Sales Manager' },
  { id: 'operations', label: 'Operations' },
];

export default function Topbar({
  onToggleSidebar,
  onToggleMobileSidebar,
  darkMode,
  onToggleDark,
  activeRole,
  onRoleChange,
}: TopbarProps) {
  return (
    <header className="h-16 bg-card border-b border-border flex items-center gap-3 px-4 lg:px-6 flex-shrink-0 z-20">
      {/* Desktop sidebar toggle */}
      <button
        onClick={onToggleSidebar}
        className="hidden lg:flex items-center justify-center w-8 h-8 rounded-lg hover:bg-muted text-muted-foreground transition-colors btn-press"
        aria-label="Toggle sidebar"
      >
        <Menu size={18} />
      </button>

      {/* Mobile sidebar toggle */}
      <button
        onClick={onToggleMobileSidebar}
        className="flex lg:hidden items-center justify-center w-8 h-8 rounded-lg hover:bg-muted text-muted-foreground transition-colors btn-press"
        aria-label="Open sidebar"
      >
        <Menu size={18} />
      </button>

      {/* Role switcher */}
      <div className="hidden sm:flex items-center gap-1 bg-muted rounded-lg p-1">
        {roles.map((role) => (
          <button
            key={`role-${role.id}`}
            onClick={() => onRoleChange(role.id)}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-150 btn-press ${
              activeRole === role.id
                ? 'bg-card text-foreground shadow-card'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {role.label}
          </button>
        ))}
      </div>

      <div className="flex-1" />

      {/* Search */}
      <div className="hidden md:flex items-center gap-2 bg-muted rounded-lg px-3 py-2 text-muted-foreground text-sm w-48 xl:w-64 cursor-pointer hover:bg-border transition-colors">
        <Search size={14} />
        <span className="text-xs">Search metrics...</span>
        <span className="ml-auto text-xs bg-card rounded px-1.5 py-0.5 border border-border">⌘K</span>
      </div>

      {/* Notifications */}
      <button className="relative w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center text-muted-foreground transition-colors btn-press">
        <Bell size={18} />
        <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-negative" />
      </button>

      {/* Theme toggle */}
      <button
        onClick={onToggleDark}
        className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center text-muted-foreground transition-colors btn-press"
        aria-label="Toggle theme"
      >
        {darkMode ? <Sun size={18} /> : <Moon size={18} />}
      </button>

      {/* Live indicator */}
      <div className="hidden sm:flex items-center gap-2 bg-positive/10 text-positive rounded-lg px-3 py-1.5">
        <span className="w-2 h-2 rounded-full bg-positive pulse-live" />
        <span className="text-xs font-semibold">Live</span>
      </div>
    </header>
  );
}